import * as THREE from 'three';
import { FACADE_STYLES } from '../gen/buildings/facadeStyles';

/**
 * Tower facades and window panes (DECISIONS "Facades").
 * - The facade texture is a texture array, one layer per style (FACADE_STYLES), generated here on
 *   canvases. Every style keeps the 500 cm module (36 cm pier, glass from 12% to 88% of the floor) that
 *   the interiors' windows follow. Alpha is the glass mask: glass is smooth and half metallic, and it
 *   is what the glass cutout opens onto generated interiors. Mullions stay opaque.
 * - Window panes (towerWindow, lowWindow instances) show a room behind the glass by interior mapping:
 *   a ray from the eye through the pane into a virtual box room (floor, ceiling, side and back walls),
 *   coloured by room kind and a per-pane seed (office with ceiling light panels, apartment, blinds
 *   down, dark). The room is emissive (daylit rooms are dimmer than the street); the standard material
 *   adds the sky's reflection on top, strongest at grazing angles. Mullions and the frame come from the
 *   tower's style.
 * Per-pane data arrives as instanceColor: (style, seed, kind), kind 0 = office, 1 = home.
 */

const SIZE = 256;

function facadeLayer(g: CanvasRenderingContext2D, s: (typeof FACADE_STYLES)[number]): void {
  const css = (c: readonly number[], a = 1) => `rgba(${c.map((v) => Math.round(v * 255)).join(',')},${a})`;
  g.fillStyle = css(s.spandrel);
  g.fillRect(0, 0, SIZE, SIZE);
  const x0 = Math.round((36 / 500) * SIZE);
  g.fillStyle = css(s.pier);
  g.fillRect(0, 0, x0, SIZE);
  // Canvas y grows downward; texture v upward. Glass 12%..88% of the floor.
  const y0 = Math.round(0.12 * SIZE);
  const y1 = Math.round(0.88 * SIZE);
  const grad = g.createLinearGradient(0, y0, 0, y1);
  grad.addColorStop(0, css(s.glass.map((c) => c * 0.55)));
  grad.addColorStop(1, css(s.glass.map((c) => c * 0.3)));
  g.fillStyle = grad;
  g.fillRect(x0, y0, SIZE - x0, y1 - y0);
  // Mullions and transom in the frame colour.
  g.fillStyle = css(s.frame);
  const w = SIZE - x0;
  for (let k = 1; k <= s.mullions; k++) g.fillRect(x0 + Math.round((w * k) / (s.mullions + 1)) - 1, y0, 3, y1 - y0);
  if (s.transom) g.fillRect(x0, Math.round(y0 + (y1 - y0) * 0.3), w, 3);
  g.fillRect(x0, y0, 3, y1 - y0);
  g.fillRect(x0, y0, w, 3);
  g.fillRect(x0, y1 - 3, w, 3);
}

/** RGBA array texture: one facade style per layer, alpha = glass. */
export function facadeArray(): THREE.DataArrayTexture {
  const n = FACADE_STYLES.length;
  const data = new Uint8Array(SIZE * SIZE * 4 * n);
  const c = document.createElement('canvas');
  c.width = c.height = SIZE;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  FACADE_STYLES.forEach((s, i) => {
    g.clearRect(0, 0, SIZE, SIZE);
    facadeLayer(g, s);
    const px = g.getImageData(0, 0, SIZE, SIZE).data;
    // Glass mask: the glass rectangle minus the mullions (recomputed exactly, not from colours).
    const x0 = Math.round((36 / 500) * SIZE);
    const y0 = Math.round(0.12 * SIZE);
    const y1 = Math.round(0.88 * SIZE);
    const w = SIZE - x0;
    const mull = (x: number) => {
      for (let k = 1; k <= s.mullions; k++) if (Math.abs(x - (x0 + Math.round((w * k) / (s.mullions + 1)))) <= 1) return true;
      return x < x0 + 3;
    };
    for (let y = 0; y < SIZE; y++)
      for (let x = 0; x < SIZE; x++) {
        const o = (y * SIZE + x) * 4;
        const glass = x >= x0 && y >= y0 + 3 && y < y1 - 3 && !mull(x) && !(s.transom && Math.abs(y - Math.round(y0 + (y1 - y0) * 0.3) - 1) <= 1);
        // Flip rows: canvas top = texture v 1.
        const d = (((SIZE - 1 - y) * SIZE + x) * 4) + i * SIZE * SIZE * 4;
        data[d] = px[o];
        data[d + 1] = px[o + 1];
        data[d + 2] = px[o + 2];
        data[d + 3] = glass ? 255 : 0;
      }
  });
  const t = new THREE.DataArrayTexture(data, SIZE, SIZE, n);
  t.format = THREE.RGBAFormat;
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = 8;
  t.needsUpdate = true;
  return t;
}

/**
 * Facade material: style layer from the facade vertices' surface attribute, roughness and metalness
 * from the glass mask. With `cut` = [yMin, yMax], glass between those world heights turns into tinted,
 * partly transparent glass over generated interiors (the transparent pass, after the opaque interiors).
 */
export function facadeMaterial(tex: THREE.DataArrayTexture, cut?: { value: THREE.Vector2 }): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 1, metalness: 1, envMapIntensity: 3.5 });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uFacade = { value: tex };
    if (cut) sh.uniforms.uCut = cut;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float surface;\nvarying vec2 vFacUv;\nvarying float vFacStyle;\nvarying float vCutY;')
      .replace('#include <project_vertex>', '#include <project_vertex>\nvFacUv = uv;\nvFacStyle = surface;\nvCutY = (modelMatrix * vec4(transformed, 1.0)).y;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform sampler2DArray uFacade;\n${cut ? 'uniform vec2 uCut;' : ''}\nvarying vec2 vFacUv;\nvarying float vFacStyle;\nvarying float vCutY;`)
      .replace(
        '#include <map_fragment>',
        `vec4 facS = texture(uFacade, vec3(vFacUv, floor(vFacStyle + 0.5)));
        diffuseColor.rgb *= facS.rgb;
        ${
          cut
            ? `if (vCutY > uCut.x && vCutY < uCut.y && facS.a > 0.5) {
          // Tinted glass, a little more opaque toward the top of each pane (a cheap sky reflection).
          float fv = fract(vFacUv.y);
          diffuseColor.rgb = mix(vec3(0.16, 0.22, 0.3), vec3(0.4, 0.5, 0.6), smoothstep(0.45, 0.88, fv));
          diffuseColor.a = mix(0.42, 0.68, smoothstep(0.45, 0.88, fv));
        }`
            : ''
        }`,
      )
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nroughnessFactor = mix(0.82, 0.06, facS.a);')
      .replace('#include <metalnessmap_fragment>', '#include <metalnessmap_fragment>\nmetalnessFactor = mix(0.05, 0.5, facS.a);');
  };
  m.customProgramCacheKey = () => (cut ? 'facadeCut' : 'facade');
  if (cut) m.transparent = true;
  return m;
}

/** Room kinds and their colours (linear): walls, floor, ceiling. */
const ROOM_GLSL = /* glsl */ `
float paneHash(float a, float b) { return fract(sin(mod(a * 91.7 + b * 47.3, 6283.0)) * 43758.5453); }

// Colour of the virtual room seen along ray f + t d (pane-local metres; x along the pane, y up, z out
// of the building), and whether the pixel is frame or mullion.
vec3 roomColour(vec3 f, vec3 d, vec2 size, vec3 data, out float frame) {
  // Flat varyings, and rounded anyway: the hash amplifies the smallest difference into another room.
  float seed = floor(data.y * 4096.0 + 0.5);
  float kind = data.z;
  float style = data.x;
  frame = 0.0;
  // Frame: 5 cm border; mullions by style (tower panes only).
  vec2 uv = f.xy / size;
  vec2 edge = min(f.xy, size - f.xy);
  if (min(edge.x, edge.y) < 0.05) frame = 1.0;
  if (kind < 0.5) {
    float m = style < 0.5 ? 2.0 : style < 1.5 ? 1.0 : style < 2.5 ? 3.0 : style < 3.5 ? 1.0 : style < 4.5 ? 2.0 : 2.0;
    float cell = fract(uv.x * (m + 1.0));
    if (min(cell, 1.0 - cell) * size.x / (m + 1.0) < 0.03) frame = 1.0;
    bool transom = style > 0.5 && style < 1.5 || style > 1.5 && style < 2.5 || style > 4.5;
    if (transom && abs(uv.y - 0.7) * size.y < 0.03) frame = 1.0;
  }
  float h1 = paneHash(seed, 1.0);
  float h2 = paneHash(seed, 2.0);
  float h3 = paneHash(seed, 3.0);
  // Room box: floor below the pane bottom, ceiling, side walls a little past the pane, depth by seed.
  float floorY = kind < 0.5 ? -0.6 : -0.6;
  float ceilY = kind < 0.5 ? 2.44 : 2.0;
  float x0 = kind < 0.5 ? -0.8 : -1.2;
  float x1 = size.x + (kind < 0.5 ? 0.8 : 1.2);
  float depth = mix(3.5, 7.0, h1);
  vec3 dd = d;
  float tx = dd.x > 0.0 ? (x1 - f.x) / dd.x : (x0 - f.x) / min(dd.x, -1e-4);
  float ty = dd.y > 0.0 ? (ceilY - f.y) / dd.y : (floorY - f.y) / min(dd.y, -1e-4);
  float tz = (-depth - f.z) / min(dd.z, -1e-4);
  float t = min(tx, min(ty, tz));
  vec3 p = f + dd * t;
  // Palette.
  vec3 wall = kind < 0.5 ? mix(vec3(0.78, 0.77, 0.74), vec3(0.62, 0.66, 0.7), h2) : mix(vec3(0.86, 0.76, 0.6), vec3(0.72, 0.78, 0.8), h2);
  vec3 floorC = kind < 0.5 ? vec3(0.22, 0.24, 0.27) : mix(vec3(0.42, 0.28, 0.17), vec3(0.5, 0.45, 0.4), h3);
  vec3 ceilC = vec3(0.86);
  vec3 c;
  float light = 1.0;
  if (t == tz) {
    c = wall;
    // Back wall: a desk or sofa band and a picture, as darker blocks.
    float band = step(p.y, floorY + (kind < 0.5 ? 1.25 : 0.9)) * step(floorY + 0.05, p.y);
    float blocks = step(0.35, fract(p.x * (kind < 0.5 ? 0.6 : 0.35) + h3));
    c = mix(c, kind < 0.5 ? vec3(0.3, 0.3, 0.32) : vec3(0.45, 0.3, 0.25), band * blocks * 0.8);
    float pic = step(abs(p.x - size.x * 0.5 - (h2 - 0.5)), 0.4) * step(abs(p.y - 1.4), 0.3) * step(0.5, h1);
    c = mix(c, vec3(0.3, 0.35, 0.45), pic * 0.8);
  } else if (t == tx) {
    c = wall * 0.85;
  } else if (dd.y < 0.0) {
    c = floorC;
  } else {
    c = ceilC;
    if (f.y > ceilY) c = vec3(0.18); // above the ceiling: the plenum behind spandrel glass
    else if (kind < 0.5) {
      // Office light panels: 60 cm squares on a 2.4 m grid.
      vec2 g = fract(p.xz / 2.4);
      if (max(abs(g.x - 0.5), abs(g.y - 0.5)) < 0.13) { c = vec3(3.0); light = 1.0; }
    }
  }
  // Depth falloff, and near the window brighter (daylight).
  float depthK = clamp(-p.z / depth, 0.0, 1.0);
  c *= mix(1.0, 0.45, depthK);
  // Variants: dark room, blinds down part way.
  if (h2 > 0.82) c *= 0.12;
  float blinds = h3 > 0.7 ? mix(0.25, 1.0, paneHash(seed, 4.0)) : 0.0;
  if (uv.y > 1.0 - blinds) {
    float slat = step(0.5, fract(f.y * 18.0));
    c = mix(vec3(0.72, 0.7, 0.66), vec3(0.6, 0.58, 0.55), slat) * 0.8;
  }
  // Home windows: curtains at the sides.
  if (kind > 0.5 && h1 > 0.3 && min(uv.x, 1.0 - uv.x) < 0.18) c = mix(vec3(0.55, 0.25, 0.2), vec3(0.75, 0.7, 0.55), h3) * 0.7;
  return c * light;
}`;

/**
 * Window pane material with rooms behind the glass. `glass` tints the room (towers' styles tint more
 * strongly); `brightness` scales the room's emission relative to the exterior's exposure.
 */
export function windowMaterial(brightness = 0.55): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ color: 0x0a0d10, roughness: 0.05, metalness: 0, envMapIntensity: 4 });
  const bright = { value: brightness };
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uRoomBright = bright;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vPaneF;\nvarying vec3 vPaneRay;\nflat varying vec2 vPaneSize;\nflat varying vec3 vPaneData;')
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
        #ifdef USE_INSTANCING
          mat4 paneM = modelMatrix * instanceMatrix;
        #else
          mat4 paneM = modelMatrix;
        #endif
        vec3 paneX = paneM[0].xyz;
        vec3 paneY = paneM[1].xyz;
        float paneW = max(length(paneX), 1e-4);
        float paneH = max(length(paneY), 1e-4);
        vec3 paneZ = normalize(cross(paneX, paneY));
        vec3 rel = cameraPosition - paneM[3].xyz;
        vec3 camL = vec3(dot(rel, paneX / paneW), dot(rel, paneY / paneH), dot(rel, paneZ));
        vPaneF = vec3(position.x * paneW, position.y * paneH, 0.0);
        vPaneRay = vPaneF - camL;
        vPaneSize = vec2(paneW, paneH);
        #ifdef USE_INSTANCING_COLOR
          vPaneData = instanceColor;
        #else
          vPaneData = vec3(0.0, 0.5, 0.0);
        #endif`,
      );
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform float uRoomBright;\nvarying vec3 vPaneF;\nvarying vec3 vPaneRay;\nflat varying vec2 vPaneSize;\nflat varying vec3 vPaneData;\n${ROOM_GLSL}`)
      .replace(
        '#include <color_fragment>',
        `float paneFrame;
        vec3 room = roomColour(vPaneF, normalize(vPaneRay), vPaneSize, vPaneData, paneFrame);
        diffuseColor.rgb = paneFrame > 0.5 ? vec3(0.2, 0.21, 0.23) : vec3(0.02, 0.025, 0.03);`,
      )
      .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\nif (paneFrame > 0.5) roughnessFactor = 0.45;')
      .replace('#include <metalnessmap_fragment>', '#include <metalnessmap_fragment>\nif (paneFrame > 0.5) metalnessFactor = 0.7;')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\nif (paneFrame < 0.5) totalEmissiveRadiance = room * uRoomBright;');
  };
  m.customProgramCacheKey = () => 'roomWindow';
  return m;
}
